// src/components/layout/TopBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TopBar.module.css';
import { useTheme } from '../../context/ThemeContext';
import userService from '../../services/userServices';
import { useAuthContext } from '../../App';

// Icon components
import SiameseAILogoIcon from '../icons/SiameseAILogoIcon';
import { Menu, UserCircle, Sun, Moon } from 'lucide-react';

// Components
import SearchBar from '../common/SearchBar';
import ProfileDropdown from './ProfileDropdown';

import foundryLogoDark from '../assets/Foundry_logo_dark_transparent_background.png';
import foundryLogoLight from '../assets/Foundry_logo_light_transparent_background.png';

function TopBar({ onCollapseSidebar, onToggleChatPanel, projects = [], onFileOpen, isLanding = false, chatPanelCollapsed = true }) {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuthContext();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const userIconRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  
  // Animation state for AI Assistant button
  const [aiButtonActive, setAiButtonActive] = useState(false);
  
  // Preload both logo images to prevent lag during theme switching
  useEffect(() => {
    const preloadImages = () => {
      const darkImg = new Image();
      const lightImg = new Image();
      darkImg.src = foundryLogoDark;
      lightImg.src = foundryLogoLight;
    };
    preloadImages();
  }, []);
  
  // Keep ROYGBIV animation running while chat panel is open
  useEffect(() => {
    if (!chatPanelCollapsed) {
      setAiButtonActive(true);
      // No timeout - keep animation running while panel is open
    } else {
      setAiButtonActive(false);
    }
  }, [chatPanelCollapsed]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userIconRef.current && !userIconRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsAuthenticated(false);
    try {
      await userService.logout();
    } catch (err) {}
    navigate('/login', { replace: true });
  };

  const handleDropdownSelect = (action) => {
    if (action === 'logout') {
      handleLogout();
      return;
    }
    // Only handle logout - settings is handled by navigation in ProfileDropdown
  };

  return (
    <>
    <div className={styles.topBar}>
      {/* LEFT: hamburger + brand + share + help */}
      <div className={styles.topBarLeft}>
        {!isLanding && (
          <div 
            onClick={onCollapseSidebar} 
            className={styles.topbarIconBtn}
            style={{ cursor: 'pointer' }}
          >
            <Menu size={25} />
          </div>
        )}

        {/* Logo container with smooth theme switching */}
        <div 
          onClick={() => navigate('/landing')}
          className={styles.foundryLogoContainer}
          style={{ 
            cursor: 'pointer',
            position: 'relative',
            display: 'inline-block'
          }}
        >
          <img
            src={foundryLogoDark}
            alt="Foundry | Powered by SiameseAI"
            className={styles.foundryLogo}
            style={{
              opacity: theme === 'light' ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out, box-shadow 0.2s ease',
              position: theme === 'light' ? 'absolute' : 'relative',
              top: theme === 'light' ? 0 : 'auto',
              left: theme === 'light' ? 0 : 'auto'
            }}
          />
          <img
            src={foundryLogoLight}
            alt="Foundry | Powered by SiameseAI"
            className={styles.foundryLogo}
            style={{
              opacity: theme === 'light' ? 1 : 0,
              transition: 'opacity 0.2s ease-in-out, box-shadow 0.2s ease',
              position: theme === 'light' ? 'relative' : 'absolute',
              top: theme === 'light' ? 'auto' : 0,
              left: theme === 'light' ? 'auto' : 0
            }}
          />
        </div>
      </div>

      {/* CENTER: search input */}
      {!isLanding && (
        <div className={styles.topBarCenter}>
          <SearchBar
            projects={projects}
            onFileOpen={onFileOpen}
            size="small"
            placeholder="Search files..."
            showButton={false}
            className="w-full"
          />
        </div>
      )}

      {/* RIGHT: siamese AI logos + theme toggle + user */}
      <div className={styles.topBarRight}>
        {!isLanding && (
          <div
            className={`${styles.aiToggleWrapper} ${(!chatPanelCollapsed || aiButtonActive) ? styles.active : ''} ${styles.iconPulse}`}
            onClick={onToggleChatPanel}
            title="Toggle AI Assistant"
          >
            <SiameseAILogoIcon
              className={`${styles.siameseAiSmallLogo} ${styles.iconPulse}`}
              style={{
                filter: (!chatPanelCollapsed || aiButtonActive) 
                  ? 'drop-shadow(0 0 8px var(--primary-glow, rgba(59, 130, 246, 0.6))) drop-shadow(0 0 3px var(--primary-light, rgba(59, 130, 246, 0.8)))' 
                  : 'drop-shadow(0 0 1px var(--primary-light, rgba(59, 130, 246, 0.4)))',
                opacity: (!chatPanelCollapsed || aiButtonActive) ? 1 : 0.85,
                transition: 'all 0.3s ease',
                transform: aiButtonActive ? 'scale(1.05)' : 'scale(1)'
              }}
            />
          </div>
        )}

        {/* Theme toggle button */}
        <button
          className={styles.topbarIconBtn}
          onClick={toggleTheme}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? (
            <Moon className="h-5 w-5" />
          ) : (
            <Sun className="h-5 w-5" />
          )}
        </button>

        {/* User icon with dropdown */}
        <div 
          className={styles.topbarIconBtn}
          ref={userIconRef}
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => setDropdownVisible(!dropdownVisible)}
          title="User Settings"
        >
          <UserCircle size={24} />
          <ProfileDropdown 
            className={styles.profileDropdown}
            visible={dropdownVisible}
            onVisibleChange={setDropdownVisible}
            onSelect={handleDropdownSelect}
          />
        </div>
      </div>
      </div>
    </>
  );
}

export default TopBar;
